import { isTrellisTheme } from 'foundations-theming/utils';
/**
 * These styles are used by the following components and styled-components wrappers:
 *
 * - PrivateButton: StyledButtonTextContainer
 * - LoadingButton: StyledLoadingButtonContent
 *
 * Make sure to read the PR below if you consider changing the display property.
 *
 * @see https://git.hubteam.com/HubSpot/foundations-components/pull/6634
 */
export const getSharedButtonContainerStyles = theme => ({
  display: 'flex',
  gap: theme.space['200'],
  alignItems: 'center',
  inlineSize: '100%',
  blockSize: '100%'
});
const sharedDisabledTokens = {
  backgroundColor: 'fill-primary-disabled',
  borderColor: 'border-primary-disabled',
  color: 'text-primary-disabled',
  cursor: 'auto'
};
const tertiaryLightStyles = {
  idle: {
    backgroundColor: 'fill-tertiary-default',
    borderColor: 'border-tertiary-default',
    color: 'text-tertiary-default'
  },
  hover: {
    backgroundColor: 'fill-tertiary-hover',
    borderColor: 'border-tertiary-default',
    color: 'text-tertiary-default',
    textDecoration: 'none'
  },
  active: {
    backgroundColor: 'fill-tertiary-pressed',
    borderColor: 'border-tertiary-default',
    color: 'text-tertiary-default'
  },
  disabled: {
    backgroundColor: 'fill-tertiary-disabled',
    borderColor: 'border-tertiary-disabled',
    color: 'text-primary-disabled',
    cursor: 'auto'
  }
};
export const setAsActive = useTokens => {
  useTokens.idle = useTokens.active;
  return useTokens;
};
export const getUseTokens = use => {
  const tokens = {
    primary: {
      idle: {
        backgroundColor: 'fill-primary-default',
        borderColor: 'fill-primary-default',
        color: 'text-core-on-fill-default'
      },
      hover: {
        backgroundColor: 'fill-primary-hover',
        borderColor: 'fill-primary-hover',
        color: 'text-core-on-fill-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-primary-pressed',
        borderColor: 'fill-primary-pressed',
        color: 'text-core-on-fill-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    'primary-white': {
      idle: {
        backgroundColor: 'fill-inverse-default-alt',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default-alt'
      },
      hover: {
        backgroundColor: 'fill-inverse-hover-alt',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default-alt'
      },
      active: {
        backgroundColor: 'fill-inverse-pressed-alt',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default-alt'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    secondary: {
      idle: {
        backgroundColor: 'fill-secondary-default',
        borderColor: 'border-secondary-default',
        color: 'text-secondary-default'
      },
      hover: {
        backgroundColor: 'fill-secondary-hover',
        borderColor: 'border-secondary-default',
        color: 'text-secondary-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-secondary-pressed',
        borderColor: 'border-secondary-default',
        color: 'text-secondary-default'
      },
      disabled: {
        backgroundColor: 'fill-secondary-disabled',
        borderColor: 'border-secondary-disabled',
        color: 'text-primary-disabled',
        cursor: 'auto'
      }
    },
    'secondary-ghost': {
      idle: {
        backgroundColor: 'fill-inverse-default',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default'
      },
      hover: {
        backgroundColor: 'fill-inverse-hover',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-inverse-pressed',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default'
      },
      disabled: {
        backgroundColor: 'fill-inverse-disabled',
        borderColor: 'border-inverse-disabled',
        color: 'text-primary-disabled',
        cursor: 'auto'
      }
    },
    tertiary: {
      idle: {
        backgroundColor: 'fill-transitional-tertiary-default',
        borderColor: 'border-transitional-tertiary-default',
        color: 'text-inverse-default'
      },
      hover: {
        backgroundColor: 'fill-transitional-tertiary-hover',
        borderColor: 'border-transitional-tertiary-default',
        color: 'text-inverse-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-transitional-tertiary-pressed',
        borderColor: 'border-transitional-tertiary-default',
        color: 'text-inverse-default'
      },
      disabled: {
        backgroundColor: 'fill-tertiary-disabled',
        borderColor: 'border-tertiary-disabled',
        color: 'text-primary-disabled',
        cursor: 'auto'
      }
    },
    'tertiary-light': Object.assign({}, tertiaryLightStyles),
    'tertiary-extra-light': {
      idle: {
        backgroundColor: 'fill-secondary-default',
        borderColor: 'border-tertiary-default',
        color: 'text-tertiary-default'
      },
      hover: {
        backgroundColor: 'fill-tertiary-hover',
        borderColor: 'border-tertiary-default',
        color: 'text-tertiary-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-tertiary-pressed',
        borderColor: 'border-tertiary-default',
        color: 'text-tertiary-default'
      },
      disabled: {
        backgroundColor: 'fill-tertiary-disabled',
        borderColor: 'border-tertiary-disabled',
        color: 'text-primary-disabled',
        cursor: 'auto'
      }
    },
    danger: {
      idle: {
        backgroundColor: 'fill-alert-default',
        borderColor: 'fill-alert-default',
        color: 'text-alert-on-fill-default'
      },
      hover: {
        backgroundColor: 'fill-alert-hover',
        borderColor: 'fill-alert-hover',
        color: 'text-alert-on-fill-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-alert-pressed',
        borderColor: 'fill-alert-pressed',
        color: 'text-alert-on-fill-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    transparent: {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-disabled'
      })
    },
    unstyled: {
      idle: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-on-fill-default',
        color: 'text-core-default'
      },
      hover: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-on-fill-default',
        color: 'text-core-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-on-fill-default',
        color: 'text-core-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    form: {
      idle: {
        backgroundColor: 'fill-surface-raised',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      hover: {
        backgroundColor: 'fill-surface-raised',
        borderColor: 'border-core-default',
        color: 'text-core-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-surface-raised',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    'form-on-dark': {
      idle: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      hover: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-default',
        color: 'text-core-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    link: {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default'
      })
    },
    'link-danger': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default'
      })
    },
    'link-on-dark': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default'
      })
    },
    'link-on-bright': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default'
      })
    },
    'view-group': Object.assign({}, tertiaryLightStyles)
  };
  return tokens[use];
};

/** @returns true if the button looks like a `<button>` */
export const isButtonUse = use => use === 'primary' || use === 'primary-white' || use === 'secondary' || use === 'secondary-ghost' || use === 'tertiary' || use === 'tertiary-light' || use === 'tertiary-extra-light' || use === 'danger' || use === 'transparent' || use === 'view-group';

/** @returns true if the button looks like a form `<input>` */
export const isFormUse = use => use === 'form' || use === 'form-on-dark';

/** @returns true if the button looks like a link `<a>` */
export const isLinkUse = use => use === 'link' || use === 'link-danger' || use === 'link-on-dark' || use === 'link-on-bright';

/** @returns true if the button has use `transparent` */
export const isTransparentUse = use => use === 'transparent';

/** @returns true if the button looks like an unstyled `<div>` */
export const isUnstyledUse = use => use === 'unstyled';
export const shouldButtonBeUnderlined = (use, theme) => isTrellisTheme(theme) && isLinkUse(use);

/**
 * This helper retrieves size tokens for a particular size (extra-small, small, medium).
 *
 * @param size - The current variant of the Button.
 * @param use - The current variant of the Button.
 *
 * @returns A new object containing the typography, padding, and gap tokens for a particular size.
 */
export const getSizeTokens = (size, use, floatingGroup) => {
  /**
   * Tokens for common properties across variants
   *
   * iconSize follows the same sizing increments as the ones that were applied separately to ExternalLinkIcon.
   * This ensures the same height between PrivateButtonLinks with non-ExternalLinkIcon icons (using hideExternalLinkIcon prop)
   * and PrivateButtonLinks with non-ExternalLinkIcon icons AND ExternalLinkIcon.
   */
  const tokens = {
    medium: {
      gap: '200',
      paddingBlockStart: '250',
      paddingBlockEnd: '250',
      iconSize: '200',
      typography: 'heading-50',
      paddingInlineStart: floatingGroup ? '400' : '600',
      paddingInlineEnd: floatingGroup ? '400' : '600'
    },
    small: {
      gap: '200',
      paddingBlockStart: '200',
      paddingBlockEnd: '200',
      iconSize: '100',
      typography: 'detail-100',
      paddingInlineStart: '400',
      paddingInlineEnd: '400'
    },
    'extra-small': {
      gap: '100',
      paddingBlockStart: '100',
      paddingBlockEnd: '100',
      iconSize: '100',
      typography: 'detail-100',
      paddingInlineStart: floatingGroup ? '400' : '300',
      paddingInlineEnd: floatingGroup ? '400' : '300'
    }
  };

  // Specific adjustments for 'transparent' use
  const transparentAdjustments = {
    medium: {
      paddingInlineStart: '300',
      paddingInlineEnd: '300'
    },
    small: {
      paddingInlineStart: '200',
      paddingInlineEnd: '200'
    },
    'extra-small': {
      paddingInlineStart: '100',
      paddingInlineEnd: '100'
    }
  };

  // Specific adjustments for 'form' use
  const formAdjustments = {
    medium: {
      typography: 'body-200',
      paddingInlineStart: '300',
      paddingInlineEnd: '400'
    },
    small: {
      typography: 'detail-200',
      paddingInlineStart: '200',
      paddingInlineEnd: '300'
    },
    'extra-small': {
      typography: 'detail-200',
      paddingInlineStart: '100',
      paddingInlineEnd: '200'
    }
  };
  if (use === 'transparent') {
    tokens[size] = Object.assign({}, tokens[size], transparentAdjustments[size]);
  }
  if (isFormUse(use)) {
    tokens[size] = Object.assign({}, tokens[size], formAdjustments[size], {
      width: '100%'
    });
  }
  if (isLinkUse(use)) {
    tokens[size] = Object.assign({}, tokens[size], {
      paddingBlockStart: '0',
      paddingInlineEnd: '0',
      paddingBlockEnd: '0',
      paddingInlineStart: '0'
    });
  }
  return tokens[size];
};